/**
 * Decision 142 (amended — Mark's UAT): the kanban board's column model —
 * pure, so it is unit-tested away from React.
 *
 * Grouping is by stage LABEL, not stage id. The stage map's scopes can point
 * at DIFFERENT pipelines (Sales Pipeline for direct, Partner Sales for
 * partner deals) whose equivalent stages share their name but not their id —
 * matching on ids scattered one pipeline's quotes into "No deal stage".
 * Labels merge them into one column, which is what the board is for.
 *
 * Columns: Draft first (drafts live in the portal, not the pipeline — a
 * draft with a linked deal still sits in Draft), then the stage labels in
 * pipeline display order (first occurrence wins on a shared label; EMPTY
 * columns stay visible — an empty stage is information), then any label the
 * data holds that the pipeline list doesn't (appended, first-seen — better a
 * surprise column than a vanished quote), then "No deal stage" only for
 * non-draft quotes with no stage label at all. With no stage list (HubSpot
 * unreachable → the API sent []), the data-label columns carry the board.
 *
 * Decision 145: an admin can HIDE stage labels (portal setting) — a hidden
 * stage loses its column, and any quote sitting in it folds into a single
 * trailing "Other stages" column (only rendered when occupied). Quotes are
 * never dropped by a column choice.
 */

export interface BoardRowLike {
  id: string
  status: string
  dealStageId: string | null
  dealStageLabel: string | null
}

export interface BoardColumn<R> { key: string; label: string; rows: R[] }

// ---------------------------------------------------------------- 146: pipelines

/** The stage map's scopes — which pipeline a partner's deals ride. */
export type PipelineScope = 'direct' | 'partner' | 'test'

/** One mapped pipeline, as the API sends it (decision 146). */
export interface BoardPipeline {
  id: string
  label: string
  scopes: string[]
  stages: Array<{ id: string; label: string }>
}

/** Which scope a quote's partner belongs to — mirrors the stage map's rule. */
export function scopeOf(partnerMode: string | null | undefined, isTest: boolean | null | undefined): PipelineScope {
  if (isTest) return 'test'
  return partnerMode === 'internal' ? 'direct' : 'partner'
}

/**
 * Decision 146: the board shows ONE pipeline at a time. A quote belongs to
 * the selected pipeline when its deal's stage is one of that pipeline's
 * stages; a stage that belongs to a DIFFERENT mapped pipeline routes the
 * quote there instead. A quote with no stage at all (no deal yet — drafts
 * included), or a stage no mapped pipeline knows, follows its partner's
 * SCOPE — so it still has exactly one home and can never vanish.
 */
export function rowsForPipeline<R extends BoardRowLike & { scope: PipelineScope }>(
  rows: R[], pipelines: BoardPipeline[], selectedId: string,
): R[] {
  const selected = pipelines.find((p) => p.id === selectedId)
  if (!selected) return rows
  const own = new Set(selected.stages.map((s) => s.id))
  const elsewhere = new Set(pipelines.filter((p) => p.id !== selectedId).flatMap((p) => p.stages.map((s) => s.id)))
  return rows.filter((r) => {
    if (r.dealStageId && own.has(r.dealStageId)) return true
    if (r.dealStageId && elsewhere.has(r.dealStageId)) return false
    return selected.scopes.includes(r.scope)
  })
}

/** The toggle's starting pipeline: the one serving the given scope, else the first. */
export function defaultPipelineId(pipelines: BoardPipeline[], scope: PipelineScope): string | null {
  return (pipelines.find((p) => p.scopes.includes(scope)) ?? pipelines[0])?.id ?? null
}

export function boardColumns<R extends BoardRowLike>(
  rows: R[], stages: Array<{ id: string; label: string }>, hiddenLabels: string[] = [],
): Array<BoardColumn<R>> {
  const hidden = new Set(hiddenLabels)
  const drafts = rows.filter((r) => r.status === 'draft')
  const staged = rows.filter((r) => r.status !== 'draft')
  const cols: Array<BoardColumn<R>> = [{ key: '__draft', label: 'Draft', rows: drafts }]

  const seen = new Set<string>()
  const labels: string[] = []
  for (const st of stages) {
    if (!seen.has(st.label)) { seen.add(st.label); labels.push(st.label) }
  }
  for (const r of staged) {
    if (r.dealStageLabel && !seen.has(r.dealStageLabel)) { seen.add(r.dealStageLabel); labels.push(r.dealStageLabel) }
  }
  for (const label of labels) {
    if (hidden.has(label)) continue
    cols.push({ key: `label:${label}`, label, rows: staged.filter((r) => r.dealStageLabel === label) })
  }

  // Decision 145: quotes whose stage an admin hid — one rollup column.
  const others = staged.filter((r) => r.dealStageLabel != null && hidden.has(r.dealStageLabel))
  if (others.length > 0) cols.push({ key: '__other', label: 'Other stages', rows: others })

  const unlabelled = staged.filter((r) => !r.dealStageLabel)
  if (unlabelled.length > 0) cols.push({ key: '__none', label: 'No deal stage', rows: unlabelled })
  return cols
}
